import {
  updateCables,
  updateGroupedConnectionsChildren,
  updateMotors,
  updateTransformers,
  updateWelders,
  UpdateChild,
  UpdateState,
} from "../../app/networkSlice";
import { AppDispatch } from "../../app/store";
import * as phase from "../../constants/phase";
import { Reference } from "../../model/reference/referenceTypes";
import { GroupedConnectionPoint } from "../../model/viewModel/groupedConnectionPoint";
import { RingfencedElement } from "../../model/viewModel/ringfencedElement";

export const shouldShowPhasesForm = (
  ringfencedTypes: RingfencedElement[],
  selected: string,
): boolean => {
  const shouldShow = (type: string) => {
    switch (type) {
      case "Transformers":
      case "Pole-mounted Transformer":
      case "Ground-mounted Transformer":
      case "Properties":
      case "Domestic Property":
      case "Small Commercial Property":
      case "Large Commercial Property":
      case "Industrial Property":
      case "Conductors":
      case "Mains Overhead Line":
      case "Mains Underground Cable":
      case "Service Overhead Line":
      case "Service Underground Cable":
      case "LCT":
      case "Generator":
      case "Heat Pump":
      case "EV Chargepoint":
      case "Motor":
      case "Welder":
      case "Connection":
      case "Grouped Connection":
        return true;
      default:
        return false;
    }
  };

  if (selected === "All") {
    const isNotAllowed = ringfencedTypes.some(
      (p) => !["Node", "Service End Node"].includes(p.name) && !shouldShow(p.name),
    );
    return !isNotAllowed;
  }
  return shouldShow(selected);
};

export const refreshNumberOfPhasesWarning = (
  ringfencedFiltered: RingfencedElement[],
  reference: Reference,
  numberOfPhases: string,
  setNumberOfPhasesWarning: (numberOfPhasesWarning: boolean) => void,
): void => {
  let isSet = false;

  const numberOfPhasesProperty = numberOfPhases === "Two" ? "Split" : numberOfPhases;

  if (ringfencedFiltered.some((p) => p.styles.name === "Ground-mounted Transformer")) {
    if (
      reference.transformers.types.every(
        (f) => !f[numberOfPhasesProperty.toLowerCase() + "phase"]["gmt"],
      )
    ) {
      setNumberOfPhasesWarning(true);
      isSet = true;
    }
  }
  if (ringfencedFiltered.some((p) => p.styles.name === "Pole-mounted Transformer")) {
    if (
      reference.transformers.types.every(
        (f) => !f[numberOfPhasesProperty.toLowerCase() + "phase"]["pmt"],
      )
    ) {
      setNumberOfPhasesWarning(true);
      isSet = true;
    }
  }

  if (!isSet) {
    setNumberOfPhasesWarning(false);
  }
};

export const applyPhaseChange = (
  ringfencedFiltered: RingfencedElement[],
  numberOfPhases: string,
  dispatchRedux: AppDispatch,
): void => {
  const childUpdates: UpdateChild[] = [];

  ringfencedFiltered.forEach((p) => {
    const pushUpdates = (q: GroupedConnectionPoint) => {
      if (q.numberOfPhases !== numberOfPhases) {
        childUpdates.push({
          id: p.id,
          childUpdate: { id: q.id, name: "numberOfPhases", value: numberOfPhases },
          isGroupUpdate: false,
          parentId: null,
        });
        childUpdates.push({
          id: p.id,
          childUpdate: { id: q.id, name: "numberOfPhasesIsDefault", value: false },
          isGroupUpdate: false,
          parentId: null,
        });
        childUpdates.push({
          id: p.id,
          childUpdate: { id: q.id, name: "phaseMap", value: [q.count, 0, 0, 0] },
          isGroupUpdate: false,
          parentId: null,
        });
        childUpdates.push({
          id: p.id,
          childUpdate: { id: q.id, name: "autoPhase", value: true },
          isGroupUpdate: false,
          parentId: null,
        });
        childUpdates.push({
          id: p.id,
          childUpdate: { id: q.id, name: "phaseAutoConsumers", value: q.count },
          isGroupUpdate: false,
          parentId: null,
        });
        ["1", "2", "3"].forEach((r) => {
          childUpdates.push({
            id: p.id,
            childUpdate: { id: q.id, name: `phase${r}Consumers`, value: 0 },
            isGroupUpdate: false,
            parentId: null,
          });
        });
        childUpdates.push({
          id: p.id,
          childUpdate: { id: q.id, name: "balancedLoad", value: true },
          isGroupUpdate: q.numberOfPhases !== phase.SINGLE,
          parentId: null,
        });
        childUpdates.push({
          id: p.id,
          childUpdate: { id: q.id, name: "unbalancePercent", value: 0 },
          isGroupUpdate: q.numberOfPhases !== phase.SINGLE,
          parentId: null,
        });
      }
    };

    if (p.ringfencedPoint) {
      pushUpdates(p.ringfencedPoint);
    } else {
      if (p.groupedConnectionPoints?.length > 0) {
        p.groupedConnectionPoints.forEach((q) => {
          pushUpdates(q);
        });
      }
    }
  });

  if (childUpdates.length) {
    dispatchRedux(updateGroupedConnectionsChildren(childUpdates));
  }

  const getPhaseUpdates = (assetId: string, numberOfPhases: string): UpdateState[] => {
    const updateStates: UpdateState[] = [
      {
        id: assetId,
        name: "numberOfPhases",
        value: numberOfPhases,
      },
      {
        id: assetId,
        name: "numberOfPhasesIsDefault",
        value: false,
      },
    ];
    return updateStates;
  };

  const assetGroups: any = ringfencedFiltered.reduce(
    (accumulator, currentValue) => {
      const name = currentValue.styles.name;
      accumulator[name] = [...(accumulator[name] || []), currentValue];
      return accumulator;
    },
    {} as Record<string, RingfencedElement[]>,
  );

  const allTransformers: RingfencedElement[] = (
    assetGroups["Ground-mounted Transformer"] || []
  ).concat(assetGroups["Pole-mounted Transformer"] || []);

  if (allTransformers.length > 0) {
    const updateStates = allTransformers.flatMap((c) =>
      getPhaseUpdates(c.id, numberOfPhases === phase.TWO ? "Split" : numberOfPhases),
    );
    dispatchRedux(updateTransformers(updateStates));
  }

  const allCables: RingfencedElement[] = (assetGroups["Overhead Line"] || []).concat(
    assetGroups["Underground Cable"] || [],
  );

  if (allCables.length > 0) {
    const updateStates = allCables.flatMap((c) => getPhaseUpdates(c.id, numberOfPhases));
    dispatchRedux(updateCables(updateStates));
  }

  const allWelders: RingfencedElement[] = assetGroups["Welder"] || [];

  if (allWelders.length > 0) {
    const updateStates = allWelders.flatMap((c) => getPhaseUpdates(c.id, numberOfPhases));
    dispatchRedux(updateWelders(updateStates));
  }

  const allMotors: RingfencedElement[] = assetGroups["Motor"] || [];

  if (allMotors.length > 0) {
    const updateStates = allMotors.flatMap((c) => getPhaseUpdates(c.id, numberOfPhases));
    dispatchRedux(updateMotors(updateStates));
  }
};
